import { useState } from "react";
import { Phone, Mail, MapPin, Send, Upload, CheckCircle, Printer } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";

export const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    message: "",
  });
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [quoteFile, setQuoteFile] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const insuranceOptions = [
    { id: "homeowners", label: "Homeowners Insurance" },
    { id: "auto", label: "Auto Insurance" },
    { id: "commercial", label: "Commercial Insurance" },
    { id: "life", label: "Life Insurance" },
    { id: "umbrella", label: "Umbrella Insurance" },
    { id: "renters", label: "Renters Insurance" },
    { id: "boat", label: "Boat Insurance" },
    { id: "other", label: "Other" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInsuranceTypeChange = (insuranceId) => {
    setInsuranceTypes((prev) => {
      if (prev.includes(insuranceId)) {
        return prev.filter((id) => id !== insuranceId);
      } else {
        return [...prev, insuranceId];
      }
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select a file smaller than 10MB",
          variant: "destructive",
        });
        return;
      }
      setQuoteFile(file);
    }
  };

  const handleLicenseFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select a file smaller than 10MB",
          variant: "destructive",
        });
        return;
      }
      setLicenseFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.address || !formData.phone || !formData.email) {
      toast({
        title: "Required fields missing",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Validate at least one insurance type selected
    if (insuranceTypes.length === 0) {
      toast({
        title: "Insurance type required",
        description: "Please select at least one type of insurance",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Backend integration - This will be implemented in the backend phase
      // For now, show success message
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast({
        title: "Quote request submitted!",
        description: "We'll review your information and get back to you shortly.",
      });

      // Reset form
      setFormData({
        name: "",
        address: "",
        phone: "",
        email: "",
        message: "",
      });
      setInsuranceTypes([]);
      setQuoteFile(null);
      setLicenseFile(null);
      if (document.getElementById("file-upload")) {
        document.getElementById("file-upload").value = "";
      }
      if (document.getElementById("license-upload")) {
        document.getElementById("license-upload").value = "";
      }
    } catch (error) {
      toast({
        title: "Submission failed",
        description: "Please try again or contact us directly",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      content: "(952) 938-9190",
      link: "tel:+19529389190",
    },
    {
      icon: Printer,
      title: "Fax",
      content: "(952) 444-6191",
      link: "tel:+19524446191",
    },
    {
      icon: Mail,
      title: "Email",
      content: "Justin.Schneider@Comcast.net",
      link: "mailto:Justin.Schneider@Comcast.net",
    },
    {
      icon: MapPin,
      title: "Service Areas",
      content: "MN, WI, IA, ND, SD, TN, AZ",
      link: null,
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-blue-700 text-white py-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1742522450616-a2cf0cba1274?w=1600&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-blue-700/90"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-xl text-blue-100">
              Get your free insurance quote or reach out with any questions
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="bg-primary/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <info.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-foreground">{info.title}</h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      className="text-primary hover:underline break-all"
                    >
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-muted-foreground">{info.content}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <Card className="shadow-xl border-2 border-primary/20">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50">
                <CardTitle className="text-3xl text-center text-foreground">
                  Obtain Your Insurance Quote
                </CardTitle>
                <p className="text-center text-muted-foreground mt-2">
                  Fill out the form below and we'll get back to you with a personalized quote
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="text-base">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                      className="mt-2"
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <Label htmlFor="address" className="text-base">
                      Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Main St, City, State, ZIP"
                      required
                      className="mt-2"
                    />
                  </div>

                  {/* Phone and Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-base">
                        Phone Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(123) 456-7890"
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-base">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Insurance Types */}
                  <div>
                    <Label className="text-base mb-3 block">
                      What type(s) of insurance are you interested in obtaining a quote for? <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                      {insuranceOptions.map((option) => (
                        <div key={option.id} className="flex items-center space-x-3">
                          <Checkbox
                            id={option.id}
                            checked={insuranceTypes.includes(option.id)}
                            onCheckedChange={() => handleInsuranceTypeChange(option.id)}
                          />
                          <Label
                            htmlFor={option.id}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {option.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-base">
                      Additional Information (Optional)
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your insurance needs..."
                      rows={4}
                      className="mt-2"
                    />
                  </div>

                  {/* File Upload - Insurance Declarations */}
                  <div>
                    <Label htmlFor="file-upload" className="text-base">
                      Current Insurance Declarations Page (Optional)
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Upload your current insurance documents to help us provide a more accurate quote
                    </p>
                    <div className="mt-2 flex items-center space-x-4">
                      <label
                        htmlFor="file-upload"
                        className="flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors bg-gray-50 hover:bg-gray-100"
                      >
                        <Upload className="h-5 w-5 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground">
                          {quoteFile ? quoteFile.name : "Choose file"}
                        </span>
                      </label>
                      <input
                        id="file-upload"
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        className="hidden"
                      />
                      {quoteFile && (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Accepted formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB)
                    </p>
                  </div>

                  {/* File Upload - Driver's License */}
                  <div>
                    <Label htmlFor="license-upload" className="text-base">
                      Driver's License (Optional)
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Upload a copy of your driver's license for verification purposes
                    </p>
                    <div className="mt-2 flex items-center space-x-4">
                      <label
                        htmlFor="license-upload"
                        className="flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors bg-gray-50 hover:bg-gray-100"
                      >
                        <Upload className="h-5 w-5 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground">
                          {licenseFile ? licenseFile.name : "Choose file"}
                        </span>
                      </label>
                      <input
                        id="license-upload"
                        type="file"
                        onChange={handleLicenseFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                      />
                      {licenseFile && (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Accepted formats: PDF, JPG, PNG (Max 10MB)
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full font-semibold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>Processing...</>
                    ) : (
                      <>
                        Submit Quote Request
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    By submitting this form, you agree to be contacted by Henry T. Warner Co. regarding your insurance quote.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Contact Options */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Prefer to Talk?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              We're here to answer your questions and help you find the perfect insurance coverage
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+19529389190">
                <Button size="lg" variant="outline" className="font-semibold">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Us: (952) 938-9190
                </Button>
              </a>
              <a href="mailto:Justin.Schneider@Comcast.net">
                <Button size="lg" variant="outline" className="font-semibold">
                  <Mail className="mr-2 h-5 w-5" />
                  Email Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
